import * as React from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Slider from "@mui/material/Slider"
import { styled } from "@mui/material"
import { formatTime } from "../../../utils/CommonMethods"

const StyledSlider = styled(Slider)(({ theme }) => ({
    height: 4,
    "& .MuiSlider-thumb": {
        width: 8,
        height: 8,
        transition: "0.1s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
        },
        "&:hover, &.Mui-focusVisible": {
            boxShadow: `0px 0px 0px 8px ${theme.palette.mode === "dark" ? "rgb(255 255 255 / 16%)" : "rgb(0 0 0 / 16%)"}`,
        },
        "&.Mui-active": {
            width: 20,
            height: 20,
        },
    },
    "& .MuiSlider-rail": {
        opacity: 0.28,
    },
    ...(theme.palette.mode === "dark" && {
        color: "#fff",
    }),
}))

interface PlayerSliderProps {
    totalDuration: number
    value: number
    onChange: (_event: Event, newValue: number | number[]) => void
}

const PlayerSlider: React.FC<PlayerSliderProps> = ({ totalDuration, value, onChange }) => {
    const [position, setPosition] = React.useState(value)
    const lastUpdateTimeRef = React.useRef(Date.now())
    const requestRef = React.useRef<number | undefined>(undefined)
    const previousTimeRef = React.useRef<number | undefined>(undefined)

    const duration = React.useMemo(() => totalDuration, [totalDuration])

    const animate = React.useCallback(
        (time: number) => {
            if (previousTimeRef.current !== undefined) {
                const deltaTime = time - previousTimeRef.current
                const currentTime = Date.now()
                const timeSinceLastUpdate = currentTime - lastUpdateTimeRef.current

                // Only interpolate if we're not too far ahead of the actual value
                if (timeSinceLastUpdate < 250) {
                    // 250ms threshold
                    setPosition((prevPosition) => {
                        const newPosition = prevPosition + deltaTime / 1000
                        // Ensure we don't get ahead of the actual audio position
                        return Math.min(newPosition, value)
                    })
                } else {
                    // If we haven't received an update in a while, sync to the actual value
                    setPosition(value)
                }
            }
            previousTimeRef.current = time
            requestRef.current = requestAnimationFrame(animate)
        },
        [value],
    )

    React.useEffect(() => {
        requestRef.current = requestAnimationFrame(animate)
        return () => cancelAnimationFrame(requestRef.current!)
    }, [animate])

    React.useEffect(() => {
        setPosition(value)
        lastUpdateTimeRef.current = Date.now()
    }, [value])

    const handleChange = (_event: Event, newValue: number | number[]) => {
        const boundedValue = Math.min(Math.max(newValue as number, 0), duration)
        setPosition(boundedValue)
        onChange(_event, boundedValue)
    }

    return (
        <Box sx={{ width: "100%", overflow: "hidden", position: "relative", p: 2 }}>
            <StyledSlider
                aria-label="time-indicator"
                size="small"
                value={position}
                min={0}
                step={0.01}
                max={duration}
                onChange={handleChange}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: -2 }}>
                <Typography variant="caption" color="text.secondary">
                    {formatTime(position)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatTime(duration)}
                </Typography>
            </Box>
        </Box>
    )
}


export default PlayerSlider

